import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { I18nProvider } from "@/i18n";
import { Toaster } from "@/components/ui/sonner";

import { PublicLayout } from "@/components/layout/PublicLayout";
import { AppLayout } from "@/components/layout/AppLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { GuideProvider } from "@/lib/guide/GuideProvider";
import { AnalyticsPageview } from "@/components/AnalyticsPageview";
import { SmartRedirect } from "@/components/SmartRedirect";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Forgot from "@/pages/Forgot";
import Reset from "@/pages/Reset";
import AuthCallback from "@/pages/AuthCallback";

import { publicRoutes } from "@/routes/publicRoutes";
import { clientRoutes } from "@/routes/clientRoutes";
import { coachRoutes } from "@/routes/coachRoutes";
import { adminRoutes } from "@/routes/adminRoutes";

// Zoom OAuth callback lands the browser on /coach/settings/zoom?zoom=... (shared
// deep-link). On web we forward it into the coach Availability page so the
// connect-status toast fires there, mirroring the Outlook return flow.
function ZoomReturn() {
  const status = new URLSearchParams(window.location.search).get("zoom") || "connected";
  return <Navigate to={`/app/coach/availability?zoom=${status}`} replace />;
}

function AppRoutes() {
  useLocation(); // re-render on navigation
  // Emergent Google OAuth returns to {origin}/app#session_id=... — intercept it
  // synchronously (before the route match) so AuthCallback runs first.
  if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route element={<PublicLayout />}>{publicRoutes}</Route>

      <Route path="/coach/settings/zoom" element={<ZoomReturn />} />

      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot" element={<Forgot />} />
      <Route path="/reset" element={<Reset />} />

      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <GuideProvider>
              <AppLayout />
            </GuideProvider>
          </ProtectedRoute>
        }
      >
        {clientRoutes}
        {coachRoutes}
        {adminRoutes}
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <SmartRedirect />
          <AnalyticsPageview />
          <AppRoutes />
          <Toaster position="top-center" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
